<template>
  <v-container fluid class="bi-parameters-filter pr-5 py-2 pl-0" style="background-color: var(--v-background-base) !important;">
    <ExpansionPanel
      :always-visible-values="alwaysVisibleFiltersKey"
      :auto-search-on-always-visible-change="false"
      :expand-button-hover-text-expanded="$t('biControlPanel.filters.expandedTooltip')"
      :expand-button-hover-text-collapsed="$t('biControlPanel.filters.collapsedTooltip')"
      :show-expand-icon="false"
      :start-expanded="true"
      @on-clear="clearFilters"
      @on-search="searchData"
    >
      <template #always-visible>
        <v-col cols="12" sm="6" md="3">
          <PaginatedAutocomplete
            :value="parametersFinancialGroup"
            :fetch="fetchFinancialGroups"
            :refresh-list-items="refreshFinancialGroups"
            item-text="name"
            item-value="id"
            :label="$t('biControlPanel.filters.financialGroupRequired')"
            :no-data-text="$t('biControlPanel.filters.noData')"
            :end-text="$t('biControlPanel.filters.endText')"
            :min-search-chars="0"
            :page-size="20"
            hide-details
            v-bind="autocompleteAttrs"
            @input="setParametersFilter('financialGroup', $event)"
            @listUpdated="refreshFinancialGroups = false"
            @error="handleFilterOptionsError"
          />
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <PaginatedAutocomplete
            :value="parametersCarrier"
            :fetch="fetchCarriers"
            :refresh-list-items="refreshCarriers"
            item-text="name"
            item-value="id"
            :label="$t('biControlPanel.filters.carrier')"
            :no-data-text="$t('biControlPanel.filters.noData')"
            :end-text="$t('biControlPanel.filters.endText')"
            :disabled="!parametersFinancialGroup"
            :min-search-chars="0"
            :page-size="20"
            hide-details
            v-bind="autocompleteAttrs"
            @input="setParametersFilter('carrier', $event)"
            @listUpdated="refreshCarriers = false"
            @error="handleFilterOptionsError"
          />
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <PaginatedAutocomplete
            :value="parametersContract"
            :fetch="fetchContracts"
            :refresh-list-items="refreshContracts"
            item-text="name"
            item-value="id"
            :label="$t('biControlPanel.filters.contract')"
            :no-data-text="$t('biControlPanel.filters.noData')"
            :end-text="$t('biControlPanel.filters.endText')"
            :disabled="!parametersFinancialGroup"
            :min-search-chars="0"
            :page-size="10"
            hide-details
            v-bind="autocompleteAttrs"
            @input="setParametersFilter('contract', $event)"
            @listUpdated="refreshContracts = false"
            @error="handleFilterOptionsError"
          />
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <PaginatedAutocomplete
            :value="parametersSubcontract"
            :fetch="fetchSubcontracts"
            :refresh-list-items="refreshSubcontracts"
            item-text="name"
            item-value="id"
            :label="$t('biControlPanel.filters.subcontract')"
            :no-data-text="$t('biControlPanel.filters.noData')"
            :end-text="$t('biControlPanel.filters.endText')"
            :disabled="!parametersContract"
            :min-search-chars="0"
            :page-size="20"
            hide-details
            v-bind="autocompleteAttrs"
            @input="setParametersFilter('subcontract', $event)"
            @listUpdated="refreshSubcontracts = false"
            @error="handleFilterOptionsError"
          />
        </v-col>
      </template>

      <template #expanded>
        <v-col cols="12" sm="6" md="3">
          <v-menu
            ref="competencyMenu"
            v-model="competencyMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                style="width: 100%"
              >
                <Textfield
                  :value="parametersCompetencyLabel"
                  v-bind="textfieldAttrs"
                  readonly
                  clearable
                  hide-details
                  style="width: 100%"
                  :label="$t('biControlPanel.filters.competency')"
                  @click:clear.stop="clearParametersCompetency"
                >
                  <template #append>
                    <v-icon size="20" color="primary">
                      mdi-calendar-month
                    </v-icon>
                  </template>
                </Textfield>
              </div>
            </template>

            <v-date-picker
              v-model="competencyDate"
              type="month"
              locale="pt-br"
              color="primary"
              no-title
              scrollable
              @change="setParametersCompetency"
            >
              <v-spacer />

              <v-btn
                text
                color="primary"
                @click="setParametersCompetency"
              >
                {{ $t('common.confirm') }}
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>
      </template>
    </ExpansionPanel>
    <SnackbarCustomize ref="SnackbarCustomize" />
  </v-container>
</template>

<script>
import moment from 'moment';
import ExpansionPanel from '@/components/BIControlPanel/ExpansionPanel.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import PaginatedAutocomplete from '@/components/Default/PaginatedAutocomplete.vue';
import Textfield from '@/components/Default/Textfield.vue';
import {
  AUTOCOMPLETE_ATTRS,
  TEXTFIELD_ATTRS,
} from '@/shared/constants/admissionMap/DefaultConstants';
import BIControlPanelModule from '@/store/modules/bi-control-panel/bi-control-panel-module';

moment.locale('pt-br');

const SEARCH_DEBOUNCE_MS = 300;

export default {
  name: 'BIParametersFilter',
  components: {
    ExpansionPanel,
    SnackbarCustomize,
    PaginatedAutocomplete,
    Textfield,
  },
  data() {
    return {
      searchTimers: {},
      competencyMenu: false,
      competencyDate: null,
      refreshFinancialGroups: false,
      refreshContracts: false,
      refreshSubcontracts: false,
      refreshCarriers: false,
    };
  },
  computed: {
    parametersFilter() {
      return BIControlPanelModule.parametersFilter;
    },
    parametersFilterOptions() {
      return BIControlPanelModule.parametersFilterOptions;
    },
    alwaysVisibleFiltersKey() {
      return [
        this.parametersFilter.financialGroup || '',
        this.parametersFilter.contract || '',
        this.parametersFilter.carrier || '',
        this.parametersFilter.subcontract || '',
      ].join('|');
    },
    autocompleteAttrs() {
      return {
        ...AUTOCOMPLETE_ATTRS,
        borderSelected: false,
        placeholder: this.translate('biControlPanel.filters.placeholder'),
      };
    },
    textfieldAttrs() {
      return {
        ...TEXTFIELD_ATTRS,
        borderSelected: false,
        placeholder: this.translate('biControlPanel.filters.placeholder'),
      };
    },
    parametersCompetencyLabel() {
      if (!this.parametersFilter.competency) {
        return null;
      }

      const competency = moment(this.parametersFilter.competency, 'YYYY-MM', true);

      if (!competency.isValid()) {
        return this.parametersFilter.competency;
      }

      const monthName = competency.format('MMMM');
      const monthCapitalized = monthName.charAt(0).toUpperCase() + monthName.slice(1);

      return `${monthCapitalized} ${competency.format('YYYY')}`;
    },
    parametersFinancialGroup: {
      get() {
        return this.parametersFilter.financialGroup;
      },
      set(value) {
        BIControlPanelModule.setParametersFilterValue({
          fieldKey: 'financialGroup',
          value: value || null,
        });
      },
    },
    parametersContract: {
      get() {
        return this.parametersFilter.contract;
      },
      set(value) {
        BIControlPanelModule.setParametersFilterValue({
          fieldKey: 'contract',
          value: value || null,
        });
      },
    },
    parametersSubcontract: {
      get() {
        return this.parametersFilter.subcontract;
      },
      set(value) {
        BIControlPanelModule.setParametersFilterValue({
          fieldKey: 'subcontract',
          value: value || null,
        });
      },
    },
    parametersCarrier: {
      get() {
        return this.parametersFilter.carrier;
      },
      set(value) {
        BIControlPanelModule.setParametersFilterValue({
          fieldKey: 'carrier',
          value: value || null,
        });
      },
    },
    parametersCompetency: {
      get() {
        return this.parametersFilter.competency;
      },
      set(value) {
        BIControlPanelModule.setParametersFilterValue({
          fieldKey: 'competency',
          value: value || null,
        });
      },
    },
  },
  watch: {
    'parametersFilter.competency': {
      immediate: true,
      handler(value) {
        this.competencyDate = value || null;
      },
    },
  },
  mounted() {
    this.runStoreAction(
      BIControlPanelModule.initializeParametersTab(),
      this.translate('biControlPanel.messages.parametersPanelLoadError'),
    );
    this.runStoreAction(
      BIControlPanelModule.loadParametersExpandedFilters(),
      this.translate('biControlPanel.messages.additionalFiltersLoadError'),
    );
  },
  beforeDestroy() {
    this.clearSearchDebounce();
  },
  methods: {
    emptyFilterOptionsResponse(page = 0, size = 20) {
      return {
        content: [],
        page,
        size,
        totalElements: 0,
        totalPages: 0,
        last: true,
      };
    },
    fetchFinancialGroups(page, size, searchText) {
      return this.fetchPaginatedFilterOptions('financialGroup', page, size, searchText);
    },
    fetchContracts(page, size, searchText) {
      if (!this.parametersFinancialGroup) {
        return Promise.resolve(this.emptyFilterOptionsResponse(page, size));
      }

      return this.fetchPaginatedFilterOptions('contract', page, size, searchText);
    },
    fetchSubcontracts(page, size, searchText) {
      if (!this.parametersContract) {
        return Promise.resolve(this.emptyFilterOptionsResponse(page, size));
      }

      return this.fetchPaginatedFilterOptions('subcontract', page, size, searchText);
    },
    fetchCarriers(page, size, searchText) {
      if (!this.parametersFinancialGroup) {
        return Promise.resolve(this.emptyFilterOptionsResponse(page, size));
      }

      return this.fetchPaginatedFilterOptions('carrier', page, size, searchText);
    },
    async fetchPaginatedFilterOptions(fieldKey, page, size, searchText) {
      const result = await this.runStoreAction(
        BIControlPanelModule.searchParametersFilterOptions({
          fieldKey,
          reset: page === 0,
          page,
          size,
          searchText: searchText || '',
        }),
        this.translate('biControlPanel.messages.filterOptionsLoadError'),
      );

      if (!result || result.success === false || !result.data) {
        return this.emptyFilterOptionsResponse(page, size);
      }

      return result.data;
    },
    setParametersFilter(fieldKey, value) {
      const normalizedValue = value || null;
      const previousValue = this.parametersFilter[fieldKey];

      BIControlPanelModule.setParametersFilterValue({
        fieldKey,
        value: normalizedValue,
      });

      if (previousValue === normalizedValue) {
        return;
      }

      this.refreshDependentFilterLists(fieldKey);
    },
    refreshDependentFilterLists(fieldKey) {
      const dependents = {
        financialGroup: ['contract', 'subcontract', 'carrier'],
        contract: ['subcontract', 'carrier'],
        carrier: [],
        subcontract: [],
      }[fieldKey] || [];

      const parentValue = this.parametersFilter[fieldKey];

      dependents.forEach((field) => {
        if (parentValue === null || parentValue === undefined) {
          BIControlPanelModule.setParametersFilterValue({
            fieldKey: field,
            value: null,
          });
        }

        this.triggerListRefresh(`refresh${this.capitalize(field)}s`);
      });
    },
    triggerListRefresh(flagName) {
      if (!Object.prototype.hasOwnProperty.call(this.$data, flagName)) {
        return;
      }

      this[flagName] = false;

      this.$nextTick(() => {
        this[flagName] = true;
      });
    },
    capitalize(value) {
      return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
    },
    handleFilterOptionsError() {
      this.showError(this.translate('biControlPanel.messages.filterOptionsLoadError'));
    },
    setParametersCompetency(value) {
      const nextValue = typeof value === 'string' ? value : this.competencyDate;

      if (nextValue) {
        this.competencyDate = nextValue;
        this.setParametersFilter('competency', nextValue);
      }

      this.competencyMenu = false;
    },
    clearParametersCompetency() {
      this.competencyDate = null;
      this.setParametersFilter('competency', null);
      this.competencyMenu = false;
    },
    ensureFieldLoaded(fieldKey) {
      const fieldState = this.parametersFilterOptions[fieldKey];

      if (fieldState.initialized) {
        return;
      }

      this.runStoreAction(
        BIControlPanelModule.searchParametersFilterOptions({
          fieldKey,
          reset: true,
          searchText: fieldState.searchInput,
        }),
        this.translate('biControlPanel.messages.filterOptionsLoadError'),
      );
    },
    getSearchInput(fieldKey) {
      return this.parametersFilterOptions[fieldKey].searchInput;
    },
    onFinancialGroupSearch(value) {
      this.queueFilterOptionsSearch('financialGroup', value);
    },
    onContractSearch(value) {
      this.queueFilterOptionsSearch('contract', value);
    },
    onSubcontractSearch(value) {
      this.queueFilterOptionsSearch('subcontract', value);
    },
    onCarrierSearch(value) {
      this.queueFilterOptionsSearch('carrier', value);
    },
    onCompetencySearch(value) {
      this.queueFilterOptionsSearch('competency', value);
    },
    queueFilterOptionsSearch(fieldKey, value) {
      const normalizedValue = value || '';

      BIControlPanelModule.setParametersFilterOptionSearchInput({
        fieldKey,
        value: normalizedValue,
      });

      if (this.searchTimers[fieldKey]) {
        clearTimeout(this.searchTimers[fieldKey]);
      }

      this.searchTimers[fieldKey] = setTimeout(() => {
        this.runStoreAction(
          BIControlPanelModule.searchParametersFilterOptions({
            fieldKey,
            reset: true,
            searchText: normalizedValue,
          }),
          this.translate('biControlPanel.messages.filterOptionsLoadError'),
        );
      }, SEARCH_DEBOUNCE_MS);
    },
    clearSearchDebounce() {
      Object.keys(this.searchTimers).forEach((fieldKey) => {
        clearTimeout(this.searchTimers[fieldKey]);
      });

      this.searchTimers = {};
    },
    canLoadMore(fieldKey) {
      const fieldState = this.parametersFilterOptions[fieldKey];

      return fieldState.pagination.hasNext
        && !fieldState.loading
        && fieldState.items.length > 0;
    },
    loadMoreFinancialGroups() {
      this.runStoreAction(
        BIControlPanelModule.searchParametersFilterOptions({
          fieldKey: 'financialGroup',
          reset: false,
        }),
        this.translate('biControlPanel.messages.filterOptionsLoadError'),
      );
    },
    loadMoreContracts() {
      this.runStoreAction(
        BIControlPanelModule.searchParametersFilterOptions({
          fieldKey: 'contract',
          reset: false,
        }),
        this.translate('biControlPanel.messages.filterOptionsLoadError'),
      );
    },
    loadMoreSubcontracts() {
      this.runStoreAction(
        BIControlPanelModule.searchParametersFilterOptions({
          fieldKey: 'subcontract',
          reset: false,
        }),
        this.translate('biControlPanel.messages.filterOptionsLoadError'),
      );
    },
    loadMoreCarriers() {
      this.runStoreAction(
        BIControlPanelModule.searchParametersFilterOptions({
          fieldKey: 'carrier',
          reset: false,
        }),
        this.translate('biControlPanel.messages.filterOptionsLoadError'),
      );
    },
    loadMoreCompetencies() {
      this.runStoreAction(
        BIControlPanelModule.searchParametersFilterOptions({
          fieldKey: 'competency',
          reset: false,
        }),
        this.translate('biControlPanel.messages.filterOptionsLoadError'),
      );
    },
    async clearFilters() {
      this.clearSearchDebounce();
      const clearResult = await this.runStoreAction(
        BIControlPanelModule.clearParametersFilters(),
        this.translate('biControlPanel.messages.filtersClearError'),
      );

      if (!clearResult || clearResult.success === false) {
        return;
      }

      [
        'refreshFinancialGroups',
        'refreshContracts',
        'refreshSubcontracts',
        'refreshCarriers',
      ].forEach((flagName) => this.triggerListRefresh(flagName));
    },
    async searchData() {
      await this.runStoreAction(
        BIControlPanelModule.submitParametersFilters(),
        this.translate('biControlPanel.messages.dataSearchError'),
      );
    },
    translate(key) {
      return this.$t(key).toString();
    },
    showError(message) {
      this.$refs.SnackbarCustomize.open('error', message);
    },
    async runStoreAction(actionPromise, fallbackMessage) {
      const result = await actionPromise;

      if (result && result.success === false) {
        this.showError(result.errorMessage || fallbackMessage);
      }

      return result;
    },
  },
};
</script>

<style scoped>
.bi-parameters-filter ::v-deep .autocomplete-default > .v-input__control > .v-input__slot,
.bi-parameters-filter ::v-deep .textfield-default > .v-input__control > .v-input__slot {
  min-height: 42px;
  padding: 0 8px !important;
}

.bi-parameters-filter ::v-deep .autocomplete-default > .v-input__control > .v-input__slot > .v-select__slot,
.bi-parameters-filter ::v-deep .textfield-default > .v-input__control > .v-input__slot > .v-text-field__slot {
  align-items: center;
  min-height: 100%;
}

.bi-parameters-filter ::v-deep .autocomplete-default > .v-input__control > .v-input__slot > .v-select__slot > .v-label,
.bi-parameters-filter ::v-deep .textfield-default > .v-input__control > .v-input__slot > .v-text-field__slot > .v-label {
  font-size: 12px;
  line-height: 20px;
}

.bi-parameters-filter ::v-deep .autocomplete-default:not(.v-input--is-focused):not(.v-input--is-label-active) > .v-input__control > .v-input__slot > .v-select__slot > .v-label,
.bi-parameters-filter ::v-deep .textfield-default:not(.v-input--is-focused):not(.v-input--is-label-active) > .v-input__control > .v-input__slot > .v-text-field__slot > .v-label {
  top: 50% !important;
  transform: translateY(-50%);
}

.bi-parameters-filter ::v-deep .autocomplete-default > .v-input__control > .v-input__slot > .v-select__slot input,
.bi-parameters-filter ::v-deep .textfield-default > .v-input__control > .v-input__slot > .v-text-field__slot > input {
  font-size: 13px;
  line-height: 20px;
}

.bi-parameters-filter ::v-deep .textfield-default :is([class*="v-input__prepend"], [class*="v-input__append"]) {
  height: 42px !important;
}
</style>
